// أنواع الحملات الرئيسية
export enum PromotionType {
  AUTOMATIC = "automatic",      // حملة تلقائية
  COUPON = "coupon"            // كوبون
}

// أنواع الحملات التفصيلية
export enum PromotionSchemeType {
  BUY_X_GET_Y = "buy_x_get_y",     // اشتر X واحصل على Y
  PRODUCT_DISCOUNT = "product_discount",  // خصم على المنتجات
  TAX_DISCOUNT = "tax_discount",    // خصم على الضريبة
  ORDER_DISCOUNT = "order_discount", // خصم على الطلب
  COMBO = "combo",              // كومبو (مجموعة منتجات)
  TARGET_PRICE = "target_price"     // سعر مستهدف
}

// واجهة صفحة الفاتورة الديناميكية
export interface InvoicePage {
  _id: string;
  sequentialId: number;
  name: string;
  type: string;
  enableMarketing: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// نوع الهدف - يمكن أن يكون منتجًا أو متغير منتج أو تصنيفًا
export enum TargetType {
  PRODUCT = "product",                    // منتج كامل بجميع متغيراته
  PRODUCT_VARIANT = "product_variant",    // متغير منتج محدد
  CATEGORY = "category"                   // تصنيف كامل
}

// واجهة متغير المنتج المستخدمة في التسويق
export interface MarketingProductVariant {
  _id: string;
  productId: string;
  productName?: string; // اسم المنتج
  sku: string;
  barcode: string;
  attributes: {
    name: string;
    value: string;
  }[];
  price: number;
  costPrice: number;
  stock: number;
  unit?: string; // وحدة القياس (كيلو، قطعة، لتر، إلخ)
  targetPrice?: number; // السعر المستهدف لهذا المنتج (للاستخدام في حملات السعر المستهدف)
}

// واجهة الهدف (المنتج/المتغير/التصنيف)
export interface PromotionTarget {
  targetType: TargetType;         // نوع الهدف
  targetId: string;               // معرف الهدف (المنتج/المتغير/التصنيف)
  quantity?: number;              // الكمية (إذا كانت مطلوبة)
}

// واجهة الحملة الأساسية
export interface Promotion {
  _id: string;
  name: string;                           // اسم الحملة
  description: string;                    // وصف الحملة
  promotionType: PromotionType;           // نوع الحملة (تلقائية أو كوبون)
  schemeType: PromotionSchemeType;        // نوع الآلية
  startDate: Date;                        // تاريخ بداية الحملة
  endDate: Date;                          // تاريخ نهاية الحملة
  code?: string;                          // رمز الكوبون (في حالة الكوبون فقط)
  maxUses?: number;                       // الحد الأقصى للاستخدام (في حالة الكوبون)
  currentUses?: number;                   // عدد مرات الاستخدام الحالية (في حالة الكوبون)
  applicableInvoicePages: string[];  // معرفات صفحات الفواتير المطبقة عليها
  isActive: boolean;                      // حالة الحملة
  scheme?: PromotionScheme;               // تفاصيل آلية الحملة (للتوافق مع النسخة القديمة)
  // الحقول الجديدة لكل نوع حملة
  buyXGetYScheme?: BuyXGetYScheme;
  productDiscountScheme?: ProductDiscountScheme;
  taxDiscountScheme?: TaxDiscountScheme;
  orderDiscountScheme?: OrderDiscountScheme;
  comboScheme?: ComboScheme;
  targetPriceScheme?: TargetPriceScheme;
  generatedCouponsCount?: number;         // عدد الكوبونات المُنشأة تلقائياً من هذه الحملة
  createdAt: Date;
  updatedAt: Date;
}

// واجهة لآليات الحملات المختلفة (الواجهة العامة)
export type PromotionScheme = 
  BuyXGetYScheme | 
  ProductDiscountScheme | 
  TaxDiscountScheme | 
  OrderDiscountScheme | 
  ComboScheme | 
  TargetPriceScheme;

// آلية اشتر X واحصل على Y
export interface BuyXGetYScheme {
  primaryTarget: PromotionTarget;       // الهدف الأساسي X (منتج أو متغير منتج أو تصنيف)
  secondaryTarget: PromotionTarget;     // الهدف الثانوي Y (منتج أو متغير منتج أو تصنيف)
  secondaryDiscount: number;            // نسبة الخصم على Y (0-100)
}

// آلية خصم على المنتجات
export interface ProductDiscountScheme {
  targets: PromotionTarget[];           // قائمة الأهداف المشمولة (منتجات أو متغيرات أو تصنيفات)
  discountValue: number;                // قيمة الخصم
  discountType: "percentage" | "fixed";  // نوع الخصم
  maxDiscount?: number;                  // الحد الأقصى للخصم (اختياري)
}

// آلية خصم على الضريبة
export interface TaxDiscountScheme {
  targets: PromotionTarget[];           // قائمة الأهداف المشمولة (منتجات أو متغيرات أو تصنيفات)
  discountPercentage: number;           // نسبة الخصم على الضريبة
  maxDiscount?: number;                 // الحد الأقصى للخصم (اختياري)
}

// آلية خصم على الطلب
export interface OrderDiscountScheme {
  targets?: PromotionTarget[];          // المنتجات المستهدفة (اختياري - إذا لم يتم تحديدها فالحملة تنطبق على جميع المنتجات)
  minOrderAmount: number;               // الحد الأدنى لقيمة الطلب الإجمالي
  minTargetAmount?: number;             // الحد الأدنى لمجموع المنتجات المستهدفة (إذا تم تحديد منتجات مستهدفة)
  discountValue: number;                // قيمة الخصم
  discountType: "percentage" | "fixed";  // نوع الخصم
  maxDiscount?: number;                  // الحد الأقصى للخصم (اختياري)
}

// خيار الكومبو الواحد (عدد معين + سعر إجمالي)
export interface ComboOption {
  quantity: number;                     // العدد المطلوب (2، 3، 4، إلخ)
  totalPrice: number;                   // السعر الإجمالي لهذا العدد
  description?: string;                 // وصف اختياري للخيار
}

// آلية الكومبو المحدثة
export interface ComboScheme {
  products: {
    target: PromotionTarget;            // هدف (منتج أو متغير منتج)
    quantity: number;                   // الكمية المطلوبة لكل منتج
  }[];
  comboOptions: ComboOption[];          // الخيارات المتعددة للكومبو
}

// آلية السعر المستهدف
export interface TargetPriceScheme {
  targets: TargetPriceItem[];           // قائمة المنتجات مع أسعارها المستهدفة
  showOriginalPrice: boolean;           // عرض السعر الأصلي
}

// عنصر السعر المستهدف لمنتج واحد
export interface TargetPriceItem {
  target: PromotionTarget;              // الهدف (منتج أو متغير منتج)
  targetPrice: number;                  // السعر المستهدف لهذا المنتج
}

// ==================== أنواع SMS ====================

// واجهة الفرع
export interface SmsBranch {
  _id: string;
  name: string;
}

// واجهة فرع العميل (في سياق العملاء)
export interface CustomerBranch {
  id: string;
  name: string;
}

// واجهة العميل لإرسال الرسائل
export interface SmsCustomer {
  _id: string;
  name: string;
  phone: string;
  email?: string;
  branches: CustomerBranch[];
}

// واجهة استجابة العملاء
export interface CustomersResponse {
  success: boolean;
  count: number;
  total: number;
  page: number;
  pages: number;
  data: {
    customers: SmsCustomer[];
  };
}

// واجهة استجابة الفروع
export interface BranchesResponse {
  success: boolean;
  count: number;
  data: {
    branches: SmsBranch[];
  };
}